<template>
  <div
      :style="computedStyle"
      :class="computedClass"
      class="ui-full-calendar-event"
  >
    <div class="ui-full-calendar-event-body">
      <div
          v-if="isTypeDay"
          :style="computedIconStyle"
          class="ui-full-calendar-event-icon">
        <ui-icon
            :name="config.icon"
            :width="24"
            :height="24"
            :stroke="config.iconStroke"
            :fill="config.iconFill"
            fill-rule=""
        />
      </div>
      <div>
        <div class="ui-full-calendar-event-header">
          <div
              v-if="!isTypeDay"
              class="ui-full-calendar-event-header__circle"
              :style="computedCircleStyle"
          />
          <p v-if="eventTime" class="ui-full-calendar-event-header__time">{{ eventTime }}</p>
          <p v-if="eventType" class="ui-full-calendar-event-header__type">{{ eventType }}</p>
          <ui-badge
              v-if="isTypeDay && eventBadge"
              :badge="eventBadge"
          />
        </div>
        <p v-if="isTypeDay && eventDescription" class="ui-full-calendar-event-description">{{ eventDescription }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { dateFormat } from '../../libs/date'
import { TASK_STATUS_IDS, TYPE_DAY, TYPE_MONTH } from './calendarConst.js'
import UiBadge from '../badge/UiBadge.vue'
import UiIcon from '../icons/UiIcon.vue'

export default {
  name: 'UiFullCalendarEvent',
  components: { UiIcon, UiBadge },
  props: {
    event: {
      type: Object,
      default: () => ({})
    },
    type: {
      type: String,
      default: ''
    }
  },

  computed: {
    config () {
      return this.event.config
    },

    data () {
      return this.event.data
    },

    computedClass () {
      return {
        [this.type]: this.type,
        overdue: this.isOverdue
      }
    },

    isOverdue () {
      return this.eventBadge?.id === TASK_STATUS_IDS.OVERDUE
    },

    eventTime () {
      const start = dateFormat(this.event.start, 'HH:mm', false)
      const end = dateFormat(this.event.end, 'HH:mm', false)
      return !this.isTypeDay ? start : `${start} - ${end}`
    },

    eventType () {
      return this.data.type
    },

    eventId () {
      return this.event.id
    },

    eventDescription () {
      return this.data.description
    },

    eventBadge () {
      return this.data.status
    },

    isTypeMonth () {
      return this.type === TYPE_MONTH
    },

    isTypeDay () {
      return this.type === TYPE_DAY
    },

    computedStyle () {
      return `background: ${this.eventBackground};
      color: ${this.config.color};`
    },

    eventBackground () {
      const bgC = this.config.backgroundColor
      if (this.isOverdue) {
        return `repeating-linear-gradient(-72deg,${bgC},${bgC} 4px,white 4px,white 11px)`
      }
      return bgC
    },

    computedCircleStyle () {
      return `background-color: ${this.config.color};`
    },

    computedIconStyle () {
      return `background-color: ${this.config.iconBackgroundColor};`
    }
  },

  methods: {
    cancelTask () {
      this.$emit('cancel-task', this.eventId)
    },
    successTask () {
      this.$emit('success-task', this.eventId)
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-full-calendar-event {
  $parent: &;

  overflow: scroll;
  width: 100%;
  height: 100%;
  margin-bottom: 2px;
  border: 1px solid;
  border-radius: $border-radius-01;
  @include hidden-scrollbar;

  &-header {
    display: flex;
    align-items: center;

    @include children {
      margin-right: 4px;
      margin-bottom: 0;

      &:last-child {
        margin-right: 0;
      }
    }
  }

  &-header__circle {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  &-header__time {
    @include font-12;
  }

  &-header__type {
    @include font-12;
    @include ellipsis;
  }

  &-icon {
    @include flex-center;

    width: 40px;
    min-width: 40px;
    height: 40px;
    margin-right: 16px;
    border-radius: 50%;
    fill: $white;
  }

  &-actions {
    display: flex;
    align-self: flex-end;
    margin-left: auto;

    @include font-14;

    &__cancel {
      margin-right: 16px;
      color: $gray-3;
      text-decoration: underline;
    }

    &__success {
      color: $primary;
      text-decoration: underline;
    }
  }

  &.day {
    padding: 14px 16px;

    #{$parent}-body {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }

    #{$parent}-header {
      @include children {
        margin-right: 12px;

        &:last-child {
          margin-right: 0;
        }
      }
    }

    #{$parent}-description {
      @include font-14;
      margin-top: 4px;
      color: $gray-2;
    }

    #{$parent}-header__time {
      @include font-14;
    }

    #{$parent}-header__type {
      @include font-18;
      font-weight: bold;
    }
  }

  &.week {
    display: flex;
    align-items: center;
    padding: 10px;
  }

  &.month {
    padding: 4px 8px;
    border: 0;
  }
}
</style>
